import { useState } from "react";

import { Link, useNavigate } from "react-router-dom";

import dayjs from "dayjs";
import { AlertTriangle, ArrowLeft, CheckCircle2, Download } from "lucide-react";
import * as XLSX from "xlsx";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import { EmptyState } from "~/components/ui/empty-state";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { TableSkeleton } from "~/components/ui/skeletons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { ALIACE_SPA_ID } from "~/constants/companies";
import { useCompanies } from "~/hooks/contabilidad/useCompanies";
import { useCxpReconciliation } from "~/hooks/contabilidad/useCxpReconciliation";
import { formatCurrency } from "~/utils/helpers";

import type { CxpReconciliationRow } from "~/services/contabilidad/cxpReconciliationService";

const clp = (n: number) => `$${formatCurrency(n)}`;

const CUENTA_LABEL: Record<string, string> = {
  "2103001": "Nacional",
  "2103002": "Exterior",
};

type ConceptKey = keyof Pick<
  CxpReconciliationRow,
  | "saldo_inicial"
  | "facturas"
  | "nota_debito"
  | "notas_credito"
  | "pagos"
  | "ajustes"
  | "saldo_contable"
  | "listado_auxiliar"
  | "diferencia"
>;

interface ConceptDef {
  key: ConceptKey;
  label: string;
  sign: "" | "+" | "-" | "=" | "±";
  docsKey?: keyof CxpReconciliationRow;
  /** Movement rows whose figure links to the account ledger (Libro Mayor) for the desglose.
   * `detail` opens the Débitos/Créditos breakdown dialog directly for that side. */
  linkable?: boolean;
  detail?: "credit" | "debit";
  signed?: boolean; // show the value with its own +/- sign (vs magnitude)
  emphasis?: "contable" | "aux" | "dif";
}

const CONCEPTS: ConceptDef[] = [
  { key: "saldo_inicial", label: "Saldo Inicial", sign: "" },
  { key: "facturas", label: "Facturas Registradas", sign: "+", docsKey: "facturas_docs", linkable: true, detail: "credit" },
  { key: "nota_debito", label: "Notas de Débito", sign: "+", docsKey: "nota_debito_docs" },
  { key: "notas_credito", label: "Notas de Crédito", sign: "-", docsKey: "notas_credito_docs", linkable: true, detail: "debit" },
  { key: "pagos", label: "Pagos", sign: "-", docsKey: "pagos_docs", linkable: true, detail: "debit" },
  { key: "ajustes", label: "Ajustes Contables", sign: "±", signed: true },
  { key: "saldo_contable", label: "Saldo Contable", sign: "=", emphasis: "contable" },
  { key: "listado_auxiliar", label: "Listado Auxiliar de Proveedores", sign: "", emphasis: "aux" },
  { key: "diferencia", label: "Diferencia", sign: "", signed: true, emphasis: "dif" },
];

export default function ReconciliacionCxpPage() {
  const navigate = useNavigate();
  const { data: companies = [] } = useCompanies();
  const [companyId, setCompanyId] = useState<string>(ALIACE_SPA_ID);
  // Default to reconciling the current month: opening = last day of previous month, corte = today.
  const [initialDate, setInitialDate] = useState(() =>
    dayjs().startOf("month").subtract(1, "day").format("YYYY-MM-DD")
  );
  const [finalDate, setFinalDate] = useState(() =>
    dayjs().format("YYYY-MM-DD")
  );

  const {
    data: rows = [],
    isLoading,
    error,
  } = useCxpReconciliation({ initialDate, finalDate, companyId });

  const hasDiferencia = rows.some((r) => Math.abs(r.diferencia) > 1);

  // Per-concept value across accounts + a Total column.
  const colTotal = (key: ConceptKey) =>
    rows.reduce((s, r) => s + Number(r[key]), 0);
  const docsTotal = (docsKey: keyof CxpReconciliationRow) =>
    rows.reduce((s, r) => s + Number(r[docsKey]), 0);

  // Link a movement figure to the account ledger (Libro Mayor) for the desglose;
  // `detail` auto-opens the Débitos/Créditos breakdown dialog for that account.
  const mayorLink = (cuenta: string, detail?: "credit" | "debit") =>
    `/contabilidad/reportes/libro-mayor?company=${companyId}&account=${cuenta}&month=${dayjs(
      finalDate
    ).format("YYYY-MM")}${detail ? `&detail=${detail}` : ""}`;

  const amountClass = (c: ConceptDef, value: number) => {
    if (c.emphasis === "dif") {
      return Math.abs(value) > 1 ? "text-red-700 font-semibold" : "text-green-700";
    }
    if (c.signed && value < 0) return "text-red-700";
    return "";
  };
  const fmt = (c: ConceptDef, value: number) =>
    c.signed ? `${value < 0 ? "-" : ""}${clp(Math.abs(value))}` : clp(value);

  const handleExport = () => {
    if (rows.length === 0) return;
    const header = [
      "Concepto",
      ...rows.map((r) => `${r.cuenta} ${CUENTA_LABEL[r.cuenta] ?? ""}`.trim()),
      "Total",
    ];
    const body = CONCEPTS.map((c) => [
      c.label,
      ...rows.map((r) => Number(r[c.key])),
      colTotal(c.key),
    ]);
    const ws = XLSX.utils.aoa_to_sheet([header, ...body]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Reconciliacion CxP");
    XLSX.writeFile(wb, `reconciliacion-cxp-${finalDate}.xlsx`);
  };

  const filters = (
    <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
      <div className="grid gap-1.5">
        <Label htmlFor="cxp-rec-empresa">Empresa</Label>
        <Select value={companyId} onValueChange={setCompanyId}>
          <SelectTrigger id="cxp-rec-empresa" className="h-9 w-56">
            <SelectValue placeholder="Empresa" />
          </SelectTrigger>
          <SelectContent>
            {companies.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="cxp-rec-inicial">Saldo Inicial al</Label>
        <Input
          id="cxp-rec-inicial"
          type="date"
          value={initialDate}
          onChange={(e) => setInitialDate(e.target.value)}
          className="h-9 w-44"
        />
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="cxp-rec-corte">Corte al</Label>
        <Input
          id="cxp-rec-corte"
          type="date"
          value={finalDate}
          onChange={(e) => setFinalDate(e.target.value)}
          className="h-9 w-44"
        />
      </div>
    </div>
  );

  return (
    <PageContainer>
      <PageHeader
        title="Reconciliación Cuentas por Pagar"
        description="Cuadre del listado auxiliar de proveedores contra el mayor contable (2103001 / 2103002)"
        back={
          <Button
            variant="ghost"
            className="h-9"
            onClick={() => navigate("/contabilidad/reportes")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver
          </Button>
        }
        actions={
          <Button
            variant="outline"
            className="h-9"
            onClick={handleExport}
            disabled={rows.length === 0}
          >
            <Download className="mr-2 h-4 w-4" />
            Exportar
          </Button>
        }
      />

      <Card>
        <CardContent className="p-4">{filters}</CardContent>
      </Card>

      {isLoading ? (
        <TableSkeleton rows={9} columns={4} />
      ) : error ? (
        <Card>
          <CardContent className="p-0">
            <EmptyState
              icon={AlertTriangle}
              title="Error al cargar la reconciliación"
              description={(error as Error).message}
            />
          </CardContent>
        </Card>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="p-0">
            <EmptyState
              icon={AlertTriangle}
              title="Sin cuentas de proveedor"
              description="No se encontraron las cuentas 2103001 / 2103002 para esta empresa."
            />
          </CardContent>
        </Card>
      ) : (
        <Card>
          <div className="flex items-center justify-between border-b px-4 py-3">
            <p className="text-sm text-muted-foreground">
              Período de movimientos{" "}
              <span className="font-medium text-foreground">
                {dayjs(initialDate).add(1, "day").format("DD/MM/YYYY")}
              </span>{" "}
              al{" "}
              <span className="font-medium text-foreground">
                {dayjs(finalDate).format("DD/MM/YYYY")}
              </span>
            </p>
            {hasDiferencia ? (
              <span className="flex items-center gap-2 text-sm font-medium text-amber-600">
                <AlertTriangle className="h-4 w-4" />
                Diferencia detectada · Revisar
              </span>
            ) : (
              <span className="flex items-center gap-2 text-sm font-medium text-green-600">
                <CheckCircle2 className="h-4 w-4" />
                Cuadrado
              </span>
            )}
          </div>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px]" />
                  <TableHead>Concepto</TableHead>
                  {rows.map((r) => (
                    <TableHead key={r.cuenta} className="text-right">
                      <div className="font-semibold text-foreground">
                        {r.cuenta}
                      </div>
                      <div className="text-xs font-normal text-muted-foreground">
                        {CUENTA_LABEL[r.cuenta] ?? r.cuenta_nombre}
                      </div>
                    </TableHead>
                  ))}
                  <TableHead className="text-right">
                    <div className="font-semibold text-foreground">Total</div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {CONCEPTS.map((c) => {
                  const rowClass =
                    c.emphasis === "contable"
                      ? "bg-muted/60 font-semibold"
                      : c.emphasis === "aux"
                        ? "bg-muted/30"
                        : c.emphasis === "dif"
                          ? "bg-amber-50 font-semibold"
                          : "";
                  const docs = c.docsKey ? docsTotal(c.docsKey) : 0;
                  return (
                    <TableRow key={c.key} className={rowClass}>
                      <TableCell className="text-center font-mono text-muted-foreground">
                        {c.sign}
                      </TableCell>
                      <TableCell>
                        {c.label}
                        {docs > 0 && (
                          <span className="ml-2 text-xs text-muted-foreground">
                            {docs} docs
                          </span>
                        )}
                      </TableCell>
                      {rows.map((r) => {
                        const v = Number(r[c.key]);
                        return (
                          <TableCell
                            key={r.cuenta}
                            className={`text-right font-mono ${amountClass(c, v)}`}
                          >
                            {c.linkable && v !== 0 ? (
                              <Link
                                to={mayorLink(r.cuenta, c.detail)}
                                className="hover:text-primary hover:underline"
                                title={`Ver el detalle en el Libro Mayor (${r.cuenta})`}
                              >
                                {fmt(c, v)}
                              </Link>
                            ) : (
                              fmt(c, v)
                            )}
                          </TableCell>
                        );
                      })}
                      <TableCell
                        className={`text-right font-mono ${amountClass(
                          c,
                          colTotal(c.key)
                        )}`}
                      >
                        {fmt(c, colTotal(c.key))}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="py-4 text-sm text-muted-foreground">
          <p>
            <strong>Fórmula:</strong> Saldo Contable = Saldo Inicial + Facturas +
            Notas de Débito − Notas de Crédito − Pagos ± Ajustes. Se toma del
            mayor contable (cuentas 2103001 / 2103002), solo asientos{" "}
            <strong>publicados</strong>.
          </p>
        </CardContent>
      </Card>
    </PageContainer>
  );
}
